import { useState } from 'react'
import { Box, Chip, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import { FS_XS, FS_SM, ICON_XS, FS_MD, ICON_MD, ICON_SM, COLOR_PRIMARY } from '../common/theme'
import { FileTypeIcon } from './FileTypeIcon'
import FolderChip from './FolderChip'
import FancyProgressArc from './FancyProgressArc'
import DocPreviewModal from '../tabs/modals/DocPreviewModal'
import type { SourceDoc } from '../types/sourcedoc'

interface SourceDocCardProps {
  doc: SourceDoc
  // When provided, a small Close icon renders top-right and fires this on
  // click. Caller is responsible for any undo / persistence flow.
  onDelete?: () => void
}

export function SourceDocCard({ doc, onDelete }: SourceDocCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false)

  const uploadedStr = doc.uploaded
    ? (() => {
      try { return new Date(doc.uploaded).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }
      catch { return null }
    })()
    : null

  // `path` is the folder the doc lives in (e.g. "/sdfsdfs"), not a file path.
  // Normalise to a trailing-slash form for the KB navigator, and pick a label
  // from the last segment (or "Knowledge Base" at the root).
  const rawPath = doc.path ?? ''
  const folderPath: string | null = rawPath
    ? (rawPath === '/' ? '/' : (rawPath.endsWith('/') ? rawPath : rawPath + '/'))
    : null

  const folderName = folderPath
    ? (folderPath === '/' ? 'Knowledge Base' : folderPath.split('/').filter(Boolean).pop() ?? 'Knowledge Base')
    : null

  const extractionLabel = doc.isprocessing
    ? 'Extracting...'
    : doc.partial
      ? 'Partial'
      : doc.traced
        ? 'Extracted'
        : null

  const extractionColor = doc.isprocessing
    ? 'text.secondary'
    : doc.partial
      ? 'warning.main'
      : 'success.main'



  const docsha = doc.filesha ?? null

  const reasonText = doc.usefulreason?.replace(/<br\s*\/?>/gi, '\n').trim()

  return (
    <>
      <Box sx={{ p: 1, mb: 0.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>

        {/* Reason this source is relevant - top of the card, divided from the
            doc metadata below. */}
        {reasonText && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.75, mb: 0.75 }}>
              <Box sx={{
                width: 3, flexShrink: 0, borderRadius: 1,
                bgcolor: doc.usefulcontent ? 'success.main' : 'text.disabled',
                opacity: 0.7,
              }} />
              <Typography variant="caption" sx={{
                fontSize: FS_XS, opacity: 0.9, lineHeight: 1.4,
                whiteSpace: 'pre-line', wordBreak: 'break-word',
              }}>
                {reasonText}
              </Typography>
            </Box>
            <Divider sx={{ mb: 0.75 }} />
          </>
        )}

        {/* Top row: filetype + filename grouped, centered; similarity arc on right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <FileTypeIcon name={doc.filename ?? ''} />
            </Box>
            <Tooltip title="Open document">
              <Typography
                variant="caption"
                onClick={() => docsha && setPreviewOpen(true)}
                sx={{
                  fontWeight: 500, fontSize: FS_SM, lineHeight: 1.3,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  cursor: docsha ? 'pointer' : 'default',
                  '&:hover': docsha ? { color: 'primary.main', textDecoration: 'underline' } : {},
                }}
              >
                {doc.filename ?? 'Document'}
              </Typography>
            </Tooltip>
          </Box>
          {doc.simint != null && (
            <Box sx={{ flexShrink: 0 }}>
              <Tooltip title={`Question match: ${doc.simint}%`}>
                <span>
                  <FancyProgressArc
                    value={doc.simint}
                    zerona
                    icon={<FindInPageOutlinedIcon sx={{ fontSize: 16, opacity: 0.65 }} />}
                  />
                </span>
              </Tooltip>
            </Box>
          )}
          {onDelete && (
            <Tooltip title="Remove source">
              <IconButton size="small" onClick={onDelete} sx={{ p: 0.25, flexShrink: 0, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                <CloseIcon sx={{ fontSize: ICON_XS }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Meta row: folder · words · [✂ partial pages] · uploaded · extraction state */}
        {(folderName || doc.totalwords != null || doc.partialexplain || uploadedStr || extractionLabel) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.75, flexWrap: 'wrap' }}>
            {folderName && (
              <FolderChip
                name={folderName}
                isProject={!!doc.buyer}
                bold={false}
                noHover
                scale={0.8}
                testId="sourcedoc_folder_chip"
              />
            )}
            {doc.totalwords != null && (
              <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.6 }}>
                {doc.totalwords.toLocaleString()} words
              </Typography>
            )}
            {doc.partialexplain && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                {doc.partial && (
                  <Tooltip title="Partial source. Only a slice of the document was used">
                    <ContentCutIcon sx={{ fontSize: ICON_XS, color: COLOR_PRIMARY, opacity: 0.85 }} />
                  </Tooltip>
                )}
                <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.6 }}>
                  {doc.partialexplain}
                </Typography>
              </Box>
            )}
            {uploadedStr && (
              <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.6 }}>
                {uploadedStr}
              </Typography>
            )}
            {/* {extractionLabel && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: extractionColor }}>
                {extractionLabel}
              </Typography>
            )} */}
          </Box>
        )}

        {/* Status chips */}
        {(doc.usefulcontent || doc.used) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
            {doc.usefulcontent && (
              <Chip size="small" label="Relevant" sx={{ fontSize: '0.62rem', height: 16 }} color="success" variant="outlined" />
            )}
            {doc.used && (
              <Chip size="small" label="Used" sx={{ fontSize: '0.62rem', height: 16 }} color="primary" variant="outlined" />
            )}
          </Box>
        )}
      </Box>

      {docsha && (
        <DocPreviewModal open={previewOpen} docsha={docsha} onClose={() => setPreviewOpen(false)} />
      )}
    </>
  )
}
